// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import {
  coerceBoolean,
  ResponseMessageSchema,
  StatusSchema,
} from "./utilitySchemas";

export const CreateInvoiceSchema = z.object({
  status: z.string(),
});
export type CreateInvoice = z.infer<typeof CreateInvoiceSchema>;

export const InvoiceAdditionalServiceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  price: z.number(),
  isOther: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type InvoiceAdditionalService = z.infer<
  typeof InvoiceAdditionalServiceSchema
>;

export const UpdateAdditionalServiceSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  description: z.string().optional(),
  price: z.number(),
  isOther: z.boolean(),
});
export type UpdateAdditionalService = z.infer<
  typeof UpdateAdditionalServiceSchema
>;

export const DiscountCodeValidationMessageKeySchema = z.enum([
  "discountCode.applied",
  "discountCode.removed",
  "discountCode.invalid",
  "discountCode.unavailable",
  "discountCode.inapplicable",
  "discountCode.validationFailed",
]);
export type DiscountCodeValidationMessageKey = z.infer<
  typeof DiscountCodeValidationMessageKeySchema
>;

export const AppliedDiscountCodeSchema = z.object({
  code: z.string(),
  promotionCodeId: z.string().nullable(),
  couponId: z.string(),
  name: z.string().nullable(),
  amount: z.number(),
  percentOff: z.number().nullable().optional(),
  subtotalNetAfterPrepayDiscount: z.number(),
  netTotalAfterDiscount: z.number(),
  vatAmount: z.number(),
  grossTotal: z.number(),
});
export type AppliedDiscountCode = z.infer<typeof AppliedDiscountCodeSchema>;

export const ValidateDiscountCodeSchema = z.object({
  requestId: z.string().uuid(),
  isExternalRequest: z.boolean(),
  code: z.string().trim().min(1),
  subtotalNetAfterPrepayDiscount: z.number().nonnegative(),
});
export type ValidateDiscountCode = z.infer<typeof ValidateDiscountCodeSchema>;

export const ValidateDiscountCodeResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: z.object({
    valid: z.boolean(),
    messageKey: DiscountCodeValidationMessageKeySchema,
    discount: AppliedDiscountCodeSchema.nullable(),
  }),
});
export type ValidateDiscountCodeResponse = z.infer<
  typeof ValidateDiscountCodeResponseSchema
>;

export const UpdateInvoiceSchema = z.object({
  status: z.string().optional(),
  invoiceNumber: z.string().optional(),
  manualInvoiceReference: z.string().nullable().optional(),
  invoiceUrl: z.string().optional(),
  saveOnly: z.boolean().default(true),
  additionalServices: z.array(UpdateAdditionalServiceSchema).optional(),
  reminderNotificationStatus: z.enum(["ENABLED", "DISABLED"]).optional(),
  allowPromoCode: coerceBoolean().optional(),
  customPrice: z.number().nullable().optional(),
});

export type UpdateInvoice = z.infer<typeof UpdateInvoiceSchema>;

export const GetInvoiceByIdSchema = z.object({
  id: z.string().uuid(),
});
export type GetInvoiceById = z.infer<typeof GetInvoiceByIdSchema>;

export const GetInvoiceByRequestIdSchema = z.object({
  requestId: z.string().uuid(),
});
export type GetInvoiceByRequestId = z.infer<typeof GetInvoiceByRequestIdSchema>;

export const DeleteInvoiceSchema = z.object({
  id: z.string().uuid(),
});
export type DeleteInvoice = z.infer<typeof DeleteInvoiceSchema>;

export const ListInvoicesSchema = z.object({
  page: z.number().int().positive().default(1),
  perPage: z.number().int().positive().default(10),
  sortBy: z
    .enum(["createdAt", "updatedAt", "invoiceNumber"])
    .default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
  filter: z
    .object({
      invoiceNumber: z.string().optional(),
      requestId: z.string().uuid().optional(),
    })
    .optional(),
});
export type ListInvoices = z.infer<typeof ListInvoicesSchema>;

export const PaymentStatusEnum = z.enum([
  "none",
  "to pay",
  "after completion",
  "paid",
  "manual",
  "manual invoice",
]);
export type PaymentStatus = z.infer<typeof PaymentStatusEnum>;

export const InvoiceSchema = z.object({
  id: z.string(),
  status: z.string(),
  customPrice: z.number().nullable(),
  invoiceNumber: z.string().nullable(),
  manualInvoiceReference: z.string().nullable(),
  invoiceUrl: z.string().nullable(),
  stripePaymentSource: z.string().nullable().optional(),
  stripeCheckoutSessionId: z.string().nullable().optional(),
  stripePaymentIntentId: z.string().nullable().optional(),
  stripeCustomerId: z.string().nullable().optional(),
  stripePaymentMethodFamily: z.string().nullable().optional(),
  stripePaymentStatus: z.string().nullable().optional(),
  paymentLinkUsed: z.boolean(),
  allowPromoCode: z.boolean(),
  discountCode: z.string().nullable().optional(),
  discountAmount: z.number().nullable().optional(),
  discountCodeAmount: z.number().nullable().optional(),
  discountCodeName: z.string().nullable().optional(),
  discountCodeStripePromotionCodeId: z.string().nullable().optional(),
  discountCodeStripeCouponId: z.string().nullable().optional(),
  hostedInvoiceUrl: z.string(),
  hasManualInvoicePayload: z.boolean(),
  platformFeeHostedInvoiceUrl: z.string().nullable(),
  platformFeeAmount: z.number().nullable(),
  reminderNotificationStatus: z.string(),
  isSent: z.boolean(),
  additionalServices: z.array(InvoiceAdditionalServiceSchema).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Invoice = z.infer<typeof InvoiceSchema>;

export const ManualInvoicePayloadSchema = z.object({
  invoiceNumber: z.string(),
  customer: z.object({
    name: z.string(),
    email: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    companyName: z.string().optional(),
    phoneNumber: z.string().optional(),
    streetName: z.string().optional(),
    streetNumber: z.string().optional(),
    postalCode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
  }),
  items: z.array(
    z.object({
      description: z.string(),
      details: z.string().optional(),
      quantity: z.number(),
      unit_price: z.number(),
    }),
  ),
  invoice_number: z.string(),
  invoice_title: z.string().nullable().optional(),
  issue_date: z.string(),
  performance_date: z.string(),
  from_address: z.object({
    email: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
  }),
  to_address: z.object({
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    address_line1: z.string().nullable().optional(),
    address_line2: z.string().nullable().optional(),
    country: z.string().nullable().optional(),
  }),
  notes: z.string(),
  amount_paid: z.union([z.string(), z.number()]).optional(),
  total_amount: z.union([z.string(), z.number()]).optional(),
  currency: z.string().nullable().optional(),
  path: z.string(),
  replace_url: z.string(),
  isTemp: z.boolean(),
});

export type ManualInvoicePayload = z.infer<typeof ManualInvoicePayloadSchema>;

export const ManualInvoicePreviewSchema = z.object({
  path: z.object({
    publicPath: z.string(),
    privatePath: z.string(),
    year: z.string(),
    month: z.string(),
  }),
  payload: ManualInvoicePayloadSchema,
});

export type ManualInvoicePreview = z.infer<typeof ManualInvoicePreviewSchema>;

export const ManualInvoicePreviewResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: ManualInvoicePreviewSchema,
});

export type ManualInvoicePreviewResponse = z.infer<
  typeof ManualInvoicePreviewResponseSchema
>;

export const ManualInvoiceHtmlPreviewSchema = z.object({
  html: z.string(),
});

export type ManualInvoiceHtmlPreview = z.infer<
  typeof ManualInvoiceHtmlPreviewSchema
>;

export const ManualInvoiceHtmlPreviewResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: ManualInvoiceHtmlPreviewSchema,
});

export type ManualInvoiceHtmlPreviewResponse = z.infer<
  typeof ManualInvoiceHtmlPreviewResponseSchema
>;

export const ManualInvoicePreviewQuerySchema = z.object({
  isExternal: coerceBoolean(),
});

export type ManualInvoicePreviewQuery = z.infer<
  typeof ManualInvoicePreviewQuerySchema
>;

export const NormalizedManualInvoiceLineItemSchema = z.object({
  description: z.string(),
  details: z.string().nullable().optional(),
  quantity: z.number(),
  price: z.number(),
  isMainLineItem: z.boolean(),
  isOther: z.boolean(),
  isSystemGenerated: z.boolean(),
});

export type NormalizedManualInvoiceLineItem = z.infer<
  typeof NormalizedManualInvoiceLineItemSchema
>;

export const NormalizedManualInvoiceDataSchema = z.object({
  invoice: z.object({
    issueDate: z.string(),
    performanceDate: z.string().nullable(),
  }),
  customerDetails: z.object({
    title: z.string().nullable(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    companyName: z.string().nullable().optional(),
    phoneNumber: z.string().nullable(),
    streetName: z.string().nullable(),
    streetNumber: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
  }),
  lineItems: z.array(NormalizedManualInvoiceLineItemSchema),
  remark: z.string().nullable(),
});

export const NormalizedManualInvoiceDataResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: NormalizedManualInvoiceDataSchema,
});

export type NormalizedManualInvoiceData = z.infer<
  typeof NormalizedManualInvoiceDataSchema
>;

export type NormalizedManualInvoiceDataResponse = z.infer<
  typeof NormalizedManualInvoiceDataResponseSchema
>;
